<template>
  <div :class="$style.modal">
    <ModalHeader
      :handleClose="handleClose"
      headerText="Command Result Details"
      :showClose="true"
    />
    <div
      v-if="isSuccessful"
      :class="$style.statusIcon"
    >
      <i :class="[$style.commandSuccessStatus, 'jc-check-circle']"> Success</i>
    </div>
    <div
      v-else-if="command.responseTime"
      :class="$style.statusIcon"
    >
      <i :class="[$style.commandErrorStatus, 'jc-times-circle']"> Error</i>
    </div>
    <div
      v-else-if="isRunning"
      :class="$style.statusIcon"
    >
      <i :class="[$style.commandRunningStatus, 'jc-info-circle']"> Running</i>
    </div>
    <div :class="$style.modalBody">
      <div :class="$style.commandRow">
        <div :class="$style.commandGroup">
          <div>
            <label :class="$style.controlLabel">Name:</label>
            <div :class="$style.controls">
              <span>{{ command.name }}</span>
            </div>
          </div>
        </div>

        <div :class="$style.commandGroup">
          <div>
            <label :class="$style.controlLabel">Ran as User:</label>
            <div :class="$style.controls">
              <span>{{ command.user }}</span>
            </div>
          </div>
        </div>
        <div :class="$style.commandGroup">
          <div>
            <label :class="$style.controlLabel">Exit Code:</label>
            <div :class="$style.controls">
              <span>{{ command.exitCode }}</span>
            </div>
          </div>
        </div>
      </div>

      <div :class="$style.commandRow">
        <div :class="$style.commandGroup">
          <div>
            <label :class="$style.controlLabel">Server:</label>
            <div :class="$style.controls">
              <span>{{ command.system }}</span>
            </div>
          </div>
        </div>

        <div :class="$style.commandGroup">
          <div>
            <label :class="$style.controlLabel">Started:</label>
            <div :class="$style.controls">
              <span>{{ !command.requestTime ? 'N/A'
                : getFormatedDateTime(command.requestTime) }}</span>
            </div>
          </div>
        </div>

        <div :class="$style.commandGroup">
          <div>
            <label :class="$style.controlLabel">Completed:</label>
            <div :class="$style.controls">
              <span>{{ !command.responseTime ? 'In Progress'
                : getFormatedDateTime(command.responseTime) }}</span>
            </div>
          </div>
        </div>
      </div>
      <div :class="$style.commandRow">
        <div :class="$style.commandOutputGroup">
          <div>
            <label :class="$style.controlLabel">Command:</label>
            <div :class="$style.controls">
              <span>{{ command.command }}</span>
            </div>
          </div>
        </div>
      </div>
      <div :class="$style.commandRow">
        <div :class="$style.commandOutputGroup">
          <div>
            <label :class="$style.controlLabel">Files:</label>
            <div :class="$style.controls">
              <ul
                v-for="(file, index) in command.files"
                :key="index"
              >
                <li> {{ file }} </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div :class="$style.commandRow">
        <div :class="$style.commandOutputGroup">
          <label :class="$style.controlLabel">log:</label>
          <textarea
            :class="[$style.commandOutput]"
            name="output"
            :readonly="true"
            :value="formattedOutput"
          />
        </div>
      </div>
    </div>
    <ModalFooter
      :handleClose="handleClose"
      :handleMainAction="handleClose"
      mainActionText="close"
    />
  </div>
</template>

<script>
import { get } from 'lodash';
import ModalEventBus from 'components/Modal/ModalEventBus';
import ModalFooter from 'components/Modal/ModalComponents/ModalFooter.vue';
import ModalHeader from 'components/Modal/ModalComponents/ModalHeader.vue';
import moment from 'moment';

export default {
  name: 'CommandResultDetailsModal',

  components: {
    ModalFooter,
    ModalHeader,
  },

  props: {
    command: {
      type: Object,
      required: true,
    },
    isRunning: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      formattedOutput: this.handleLineBreaks(this.command.response),
    };
  },

  computed: {
    isSuccessful() { return get(this.command, 'response.data.exitCode') === 0; },
  },

  methods: {
    getFormatedDateTime(dateTime) {
      return moment.utc(dateTime).format('YYYY-MM-DD @ h:mm A');
    },

    handleClose() {
      ModalEventBus.close();
    },

    // this function is neccessary due to the original modal putting line breaks after 97 characters
    // want to keep the functionality for consistency until a decision is made to remove it.
    handleLineBreaks(responseStrings) {
      let formattedOutput = '';
      if (responseStrings) {
        if (get(responseStrings, 'data.output')) {
          formattedOutput = responseStrings.data.output.replace(/(.{97})/g, '$1\n').trim();
        }
        if (responseStrings.error) {
          formattedOutput = `${formattedOutput} \n${responseStrings.error.replace(/(.{97})/g, '$1\n').trim()}`;
        }
      }

      return formattedOutput;
    },
  },
};
</script>

<style scoped module>
@import "../components/Modal/css/ModalStyles.css";

.commandRow {
  display: flex;
}

.commandGroup {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  width: 25%;
}

.commandOutputGroup {
  margin-bottom: 12px;
  width: 100%;
}

.statusIcon {
  font-size: 16px;
  font-weight: 500;
  line-height: 23px;
  padding: 0 0 0 35px;
}

.commandOutput {
  display: inline-block;
  font-family: var(--jc-font-monospace);
  height: 200px;
  margin: 0 !important;
  resize: none;
  width: 100%;
}

.commandSuccessStatus {
  color: green;
}

.commandErrorStatus {
  color: red;
}

.commandRunningStatus {
  color: gray;
}

.controlLabel {
  color: #4d4c4b;
  font-size: 11px;
  font-weight: 500;
  line-height: 23px;
  margin: 0;
  text-transform: uppercase;
}

.controls {
  overflow-wrap: break-word;
}

.modalBody {
  height: 500px;
  max-height: 50vh;
}
</style>
